/**
 * VaultErrorState (Intelligence Studio)
 *
 * ✅ ENTERPRISE: Displays user-friendly error states for vault issues.
 * Handles: stuck processing, not found, loading timeout, connection errors.
 */

import React, { useState, useCallback } from 'react';

import {
  AlertTriangle,
  RefreshCw,
  Wrench,
  Loader2,
  ArrowLeft,
  Server,
  Clock,
  FileQuestion,
} from 'lucide-react';
import { traceFetch } from '@/pages/intelligence-studio/utils/traceContext';
import { resolveAuthToken } from '@/pages/intelligence-studio/utils/authTokens';
import { getApiBaseUrl } from '../../../config/runtimeUrls';

const VaultErrorState = ({ notebook, onRefresh, onBack, onFixStuck }) => {
  const [isFixing, setIsFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const errorType = notebook?._loadError || 'unknown';
  const errorMessage = notebook?._errorMessage || 'An error occurred while loading this vault.';
  const isStuck = notebook?._isStuck || errorType === 'timeout';

  const handleFixStuck = useCallback(async () => {
    if (!notebook?.id || isFixing) return;

    setIsFixing(true);
    setFixResult(null);

    try {
      const token = resolveAuthToken();
      const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
      const response = await traceFetch(
        `${apiBase}/intelligence-studio/notebooks/${notebook.id}/finalize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ force: true }),
        },
        {
          apiName: 'notebook.finalize',
          pipelineStage: 'http',
          notebookId: notebook.id,
        }
      );

      const result = await response.json();

      if (result?.finalized || result?.success) {
        setFixResult({ success: true, message: 'Vault fixed! Refreshing...' });
        setTimeout(() => {
          onRefresh?.();
        }, 1000);
        return;
      }

      const files = Array.isArray(notebook?.files) ? notebook.files : [];
      const reprocessTargets = files
        .map(f => ({
          fileId: f?.fileId || f?.id,
          status: String(f?.status || '').toLowerCase(),
        }))
        .filter(f => f.fileId && ['pending', 'uploading', 'queued', 'processing', 'restoring'].includes(f.status));

      if (reprocessTargets.length > 0) {
        const reprocessResults = await Promise.allSettled(
          reprocessTargets.map(({ fileId }) =>
            traceFetch(
              `${apiBase}/intelligence-studio/notebooks/${notebook.id}/files/${fileId}/reprocess`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ reason: 'stuck_vault_fix', tryCascade: true }),
              },
              {
                apiName: 'notebook.file.reprocess',
                pipelineStage: 'http',
                notebookId: notebook.id,
                fileId,
              }
            )
          )
        );
        const okCount = reprocessResults.filter(r => r.status === 'fulfilled').length;
        if (okCount > 0) {
          setFixResult({
            success: true,
            message: `Re-queued ${okCount} file${okCount === 1 ? '' : 's'} for processing. Refreshing...`,
          });
          setTimeout(() => {
            onRefresh?.();
          }, 1000);
          return;
        }
      }

      setFixResult({
        success: false,
        message:
          result?.reason ||
          result?.message ||
          'Could not fix vault. Try again or contact support.',
      });
    } catch (err) {
      setFixResult({
        success: false,
        message: `Error: ${err.message}`,
      });
    } finally {
      setIsFixing(false);
    }
  }, [notebook?.id, isFixing, onRefresh]);

  // Icon and colors based on error type
  const getErrorConfig = () => {
    switch (errorType) {
      case 'timeout':
        return {
          icon: Clock,
          title: 'Loading Timed Out',
          color: 'text-amber-500',
          bgColor: 'bg-amber-100 dark:bg-amber-900/30',
        };
      case 'not_found':
        return {
          icon: FileQuestion,
          title: 'Vault Not Found',
          color: 'text-red-500',
          bgColor: 'bg-red-100 dark:bg-red-900/30',
        };
      case 'connection':
        return {
          icon: Server,
          title: 'Connection Error',
          color: 'text-orange-500',
          bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        };
      default:
        return {
          icon: AlertTriangle,
          title: isStuck ? 'Vault Stuck' : 'Error Loading Vault',
          color: 'text-red-500',
          bgColor: 'bg-red-100 dark:bg-red-900/30',
        };
    }
  };

  const config = getErrorConfig();
  const IconComponent = config.icon;

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center">
        {/* Error Icon */}
        <div
          className={`w-20 h-20 mx-auto mb-6 rounded-full ${config.bgColor} flex items-center justify-center`}
        >
          <IconComponent className={`w-10 h-10 ${config.color}`} />
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
          {config.title}
        </h2>

        {/* Message */}
        <p className="text-sm text-slate-600 dark:text-neutral-400 mb-6 leading-relaxed">
          {errorMessage}
        </p>

        {/* Fix Result Toast */}
        {fixResult && (
          <div
            className={`mb-6 px-4 py-3 rounded-lg text-sm ${
              fixResult.success
                ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
                : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
            }`}
          >
            {fixResult.message}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {/* Fix Stuck Button (only for stuck/timeout) */}
          {(isStuck || errorType === 'timeout') && (
            <button
              onClick={handleFixStuck}
              disabled={isFixing}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isFixing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4" />
                  Fix Stuck Vault
                </>
              )}
            </button>
          )}

          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 dark:bg-neutral-800 text-slate-700 dark:text-neutral-200 rounded-lg hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>

          {/* Back Button */}
          <button
            onClick={onBack}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 dark:bg-neutral-800 text-slate-700 dark:text-neutral-200 rounded-lg hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Notebooks
          </button>
        </div>

        {/* Help Text */}
        <p className="mt-8 text-xs text-slate-400 dark:text-neutral-500">
          If this problem persists, try clearing your browser cache or contact support.
        </p>
      </div>
    </div>
  );
};

export default VaultErrorState;
